import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { studyPurposes } from '../data/purposes';
import { FiChevronRight, FiFlag, FiCpu, FiDatabase, FiLayers, FiCheck } from 'react-icons/fi';
import ScrollToTop from '../utils/ScrollToTop';

export default function PurposeSelection({ selectedId, onSelect, onNext }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);

  const steps = [
    { label: "Kognitive Daten laden", icon: FiDatabase },
    { label: "Mission-Parameter berechnen", icon: FiFlag },
    { label: "MAPS-Profil synchronisieren", icon: FiCpu },
    { label: "Strategie-Plan erstellen", icon: FiLayers },
  ];

  // Scroll nach oben wenn die Ladeanimation startet
  useEffect(() => {
    if (isGenerating) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [isGenerating]);

  const handleStartGeneration = () => {
    setIsGenerating(true);
    let current = 0;
    const interval = setInterval(() => {
      if (current < steps.length - 1) {
        current++;
        setGenStep(current);
      } else {
        clearInterval(interval);
        setTimeout(() => onNext(), 1000);
      }
    }, 800);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto pt-12 pb-20 px-4">
      <ScrollToTop />
      <AnimatePresence mode="wait">
        {!isGenerating ? (
          <motion.div key="selection" exit={{ opacity: 0, scale: 0.95 }}>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-black mb-4 tracking-widest uppercase">
                <SafeIcon icon={FiFlag} />
                <span>Schritt 3: Deine Mission</span>
              </div>
              <h2 className="text-5xl font-black text-white mb-6 tracking-tight">Was ist dein Ziel?</h2>
              <p className="text-slate-200 text-xl max-w-2xl mx-auto leading-relaxed">
                Wähle den Zweck deiner MatheMission. Dies bestimmt die Ausrichtung deines 6-Wochen-Plans.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              {Object.values(studyPurposes).map((purpose) => (
                <button
                  key={purpose.id}
                  onClick={() => onSelect(purpose.id)}
                  className={`group relative p-8 rounded-[40px] border-2 text-left transition-all duration-500 ${
                    selectedId === purpose.id
                      ? 'bg-blue-600 border-blue-400 shadow-2xl shadow-blue-500/30 scale-[1.02]'
                      : 'bg-slate-800/50 border-slate-700/60 hover:border-slate-500'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors ${selectedId === purpose.id ? 'bg-white text-blue-600' : 'bg-slate-700 text-slate-300'}`}>
                    <SafeIcon icon={purpose.icon} size={28} />
                  </div>
                  <h3 className={`text-xl font-black mb-3 ${selectedId === purpose.id ? 'text-white' : 'text-slate-100'}`}>{purpose.title}</h3>
                  <p className={`text-sm leading-relaxed ${selectedId === purpose.id ? 'text-blue-50' : 'text-slate-300'}`}>{purpose.description}</p>
                </button>
              ))}
            </div>
            <div className="flex justify-center">
              <button
                disabled={!selectedId}
                onClick={handleStartGeneration}
                className={`group px-12 py-6 rounded-3xl font-black text-xl flex items-center gap-4 transition-all ${selectedId ? 'bg-white text-slate-900 shadow-2xl hover:scale-105 active:scale-95' : 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-50'}`}
              >
                Strategie-Plan generieren
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="generating" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-20">
            <div className="w-full max-w-md bg-slate-900 border-4 border-slate-800 p-12 rounded-[48px] shadow-2xl relative overflow-hidden">
              <div className="flex flex-col gap-8 relative z-10">
                {steps.map((step, idx) => (
                  <div key={idx} className="flex items-center gap-6">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 ${genStep >= idx ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/40' : 'bg-slate-800 text-slate-500'}`}>
                      {genStep > idx ? <SafeIcon icon={FiCheck} /> : <SafeIcon icon={step.icon} />}
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm font-black uppercase tracking-widest transition-colors ${genStep >= idx ? 'text-white' : 'text-slate-500'}`}>{step.label}</div>
                      {genStep === idx && (
                        <motion.div className="h-0.5 bg-blue-500/30 mt-2 overflow-hidden" initial={{ width: 0 }} animate={{ width: "100%" }}>
                          <motion.div className="h-full bg-blue-400" animate={{ x: ["-100%", "100%"] }} transition={{ repeat: Infinity, duration: 1 }} />
                        </motion.div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <motion.p animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 2 }} className="mt-12 text-blue-300 font-mono text-xs uppercase tracking-[0.4em] font-black">
              System-Architektur wird aufgebaut...
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}